import {TopAnimalHead} from "../component/TopAnimalHead";
import {RoadKill} from "../RoadKill";
import {JW_TYPE, SYMBOL_NAME} from "../component/enums/RoadKillEnums";
import {AnimalHeadCount} from "../component/AnimalHeadCount";
import {RKHelper} from "../utils/RKHelper";
import {gsap, TimelineLite} from "gsap";
import {RoadKillModel} from "../server/RoadKillModel";
import {RoadKillGameMode} from "../server/RoadKillGameMode";
import {RoadKillSound} from "../sound/RoadKillSound";

export class TopAnimalController {
    private _sw1: TopAnimalHead;
    private _sw2: TopAnimalHead;
    private _sw3: TopAnimalHead;
    private _combinedAnimalsHeadCount: AnimalHeadCount;
    private _isSuperFSTriggered: boolean = false;
    private _totalHits: number = 0;

    constructor() {
        this.init();
    }

    public getCombinedAnimal(): AnimalHeadCount {
        return this._combinedAnimalsHeadCount;
    }

    public updateCombinedAnimalMultiToZero(): void {
        this.setTotalHits(0);
    }

    public updateTopAnimalHits(aliasName: SYMBOL_NAME, hitCount: number, isRestore: boolean = false, landed: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (this._isSuperFSTriggered || RoadKill.model.data.mode === RoadKillGameMode.SUPER_FREESPIN) {
            if (isRestore) {
                this.setTotalHits(hitCount);
            } else {
                const currentCount: number = Math.min(RoadKill.model.data.totalAnimalHitsSFS, this._totalHits + 1);
                tl.add(this.setTotalHits(currentCount));
            }
        } else {
            tl.add(() => {
                const topAnimalHead: TopAnimalHead | undefined = this.getTopAnimalHeadByAliasName(aliasName);
                if (topAnimalHead) {
                    if (isRestore) {
                        topAnimalHead.setCount(hitCount, isRestore);
                    } else {
                        let currCount: number = topAnimalHead.count;
                        if (topAnimalHead.count < hitCount && !landed) {
                            topAnimalHead.setCount(++currCount);
                        } else if (topAnimalHead.count < hitCount && landed && hitCount === 5) {
                            topAnimalHead.setCount(5);
                        }
                    }
                }
            });
        }

        return tl;
    }

    public getJWPopOutAnim(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.add([
            this.jwPopOut(this._sw1, JW_TYPE.BEAR),
            this.jwPopOut(this._sw2, JW_TYPE.DEER),
            this.jwPopOut(this._sw3, JW_TYPE.RABBIT),
        ]);
        tl.add(() => {
            RoadKill.jumpingWildView.degradeToWooden(SYMBOL_NAME.SW1);
            RoadKill.jumpingWildView.degradeToWooden(SYMBOL_NAME.SW2);
            RoadKill.jumpingWildView.degradeToWooden(SYMBOL_NAME.SW3);
        }, 0)
        return tl;
    }

    public getJWPopInAnim(jwType: JW_TYPE, isUpgraded: boolean = false, playPosterSound: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        let currentJW: TopAnimalHead | null = null;
        if (jwType === JW_TYPE.BEAR) {
            currentJW = this._sw1;
        } else if (jwType === JW_TYPE.DEER) {
            currentJW = this._sw2;
        } else if (jwType === JW_TYPE.RABBIT) {
            currentJW = this._sw3;
        }
        if (currentJW && !currentJW.visible) {
            currentJW.state.clearTracks();
            currentJW.state.setEmptyAnimation(0, 0);
            currentJW.setCount(0);
            currentJW.alpha = 0;
            currentJW.visible = true;
            currentJW.setPosition();
            tl.add(() => {
                RoadKill.soundPlayer.playTrack(RoadKillSound.PLANK_SOUND)
            });
            tl.add(currentJW?.playPopIn(isUpgraded), 0);
            if (RoadKill.model.data.nextMode !== RoadKillGameMode.SUPER_FREESPIN) {
                tl.add(currentJW?.playCountIn(), 0);
            }
            if (currentJW === this._sw1) {
                tl.add(() => {
                    RoadKill.soundPlayer.playTrack(RoadKillSound.PLANK_SOUND)
                }, 0.6)
            }
        }

        return tl;
    }

    public reset(): void {
        this.individualAnimalsArray.forEach((topAnimalHead: TopAnimalHead) => {
            if (topAnimalHead.visible && topAnimalHead?.animalHeadCount && topAnimalHead.animalHeadCount.value > 1) {
                topAnimalHead.playCountOut();
            }
            topAnimalHead.setCount(0);
        });
        this._isSuperFSTriggered = false;
        if (this._totalHits > 0) {
            RKHelper.playSpineWithTimeline(this._combinedAnimalsHeadCount, 0, "bgOut").add(() => {
                this.setTotalHits(0);
            });
        }
    }

    public onSuperFsTriggered(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._isSuperFSTriggered = true;
        this._totalHits = 0;
        this.individualAnimalsArray.forEach((topAnimalHead: TopAnimalHead) => {
            if (topAnimalHead.visible && topAnimalHead?.animalHeadCount && topAnimalHead.animalHeadCount.value > 1) {
                topAnimalHead.playCountOut();
            }
        });
        if (!this._combinedAnimalsHeadCount.visible) {
            this._combinedAnimalsHeadCount.alpha = 0;
            this._combinedAnimalsHeadCount.visible = true;
        }
        tl.add([gsap.to(this._combinedAnimalsHeadCount, {duration: 0.01, alpha: 1}),
            RKHelper.playSpineWithTimeline(this._combinedAnimalsHeadCount, 0, "bgIn")
        ]);
        return tl;
    }

    public hideCombinedAnimalsHeadCount():void{
        this.setTotalHits(0);
        RKHelper.playSpineWithTimeline(this._combinedAnimalsHeadCount, 0, "bgOut");
    }

    public onModeChange(nextMode: RoadKillGameMode): void {
        if (nextMode === RoadKillGameMode.SUPER_FREESPIN) {
            this.onSuperFsTriggered();
        } else {
            this._isSuperFSTriggered = false;
            if (this._totalHits > 0) {
                this.hideCombinedAnimalsHeadCount();
            }
        }
    }

    public setTotalHits(value: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (this._totalHits != value) {
            this._totalHits = value;
            if (this._totalHits <= 0) {
                this._combinedAnimalsHeadCount.visible = false;
            } else if (this._totalHits <= 20) {
                if (!this._combinedAnimalsHeadCount.visible) {
                    tl.add(() => {
                        this._combinedAnimalsHeadCount.visible = true;
                        this._combinedAnimalsHeadCount.alpha = 0;
                        gsap.to(this._combinedAnimalsHeadCount, {duration: 0.1, alpha: 1});
                    });
                }
                tl.add([RKHelper.playSpineWithTimeline(this._combinedAnimalsHeadCount, 0, "hit" + this._totalHits), () => {
                    if (!RoadKill.model.isRestore) {
                        RoadKill.soundPlayer.playTrack(RoadKillSound.ADD_HIT_LINE_SOUND);
                    }
                }]);
            }
        }
        return tl;
    }

    public isSuperFS(): boolean {
        const data: RoadKillModel = RoadKill.model;
        if (data.mode === RoadKillGameMode.NORMAL && data.nextMode === RoadKillGameMode.SUPER_FREESPIN) {
            return true;
        } else if (data.mode === RoadKillGameMode.FREESPIN && data.nextMode === RoadKillGameMode.SUPER_FREESPIN) {
            return true;
        } else if (data.isRestore && data.mode === RoadKillGameMode.SUPER_FREESPIN && data.nextMode === RoadKillGameMode.SUPER_FREESPIN) {
            return true;
        }
        return false;
    }

    private init() {
        this.createTopAnimalView();
    }

    private createTopAnimalView(): void {
        this._sw1 = new TopAnimalHead(RoadKill.assetLoader.getSpineAsset("bearHead"), JW_TYPE.BEAR);
        this._sw2 = new TopAnimalHead(RoadKill.assetLoader.getSpineAsset("deerHead"), JW_TYPE.DEER);
        this._sw3 = new TopAnimalHead(RoadKill.assetLoader.getSpineAsset("rabbitHead"), JW_TYPE.RABBIT);
        RoadKill.backgroundElements.animalContainer.addChild(this._sw1, this._sw2, this._sw3);
        this._combinedAnimalsHeadCount = new AnimalHeadCount(RoadKill.assetLoader.getSpineAsset("animalCollectionHeads"), JW_TYPE.COMBINED_AMINALS);
        RoadKill.topAnimalCounterView.addChild(this._combinedAnimalsHeadCount);
        this._combinedAnimalsHeadCount.position.set(360, 135);
        this._combinedAnimalsHeadCount.visible = false;
    }

    private jwPopOut(currentJW: TopAnimalHead, jwType: JW_TYPE): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (jwType === JW_TYPE.DEER) {
            currentJW = this._sw2;
        } else if (jwType === JW_TYPE.RABBIT) {
            currentJW = this._sw3;
        } else if (jwType === JW_TYPE.BEAR) {
            currentJW = this._sw1;
        }
        if (currentJW.visible) {
            tl.add(() => {
                currentJW.playPopOut();
            });
            tl.add(() => {
                currentJW.alpha = 0
                currentJW.visible = false;
                currentJW.setCount(0);
            }, 0.4);
        }

        return tl;
    }

    public getTopAnimalHeadByAliasName(aliasName: SYMBOL_NAME): TopAnimalHead | undefined {
        let jw: TopAnimalHead | undefined;
        if (aliasName === SYMBOL_NAME.SW1) {
            jw = this._sw1;
        } else if (aliasName === SYMBOL_NAME.SW2) {
            jw = this._sw2;
        } else if (aliasName === SYMBOL_NAME.SW3) {
            jw = this._sw3;
        }
        return jw;
    }

    get totalHits(): number {
        return this._totalHits;
    }

    public get individualAnimalsArray(): TopAnimalHead[] {
        return [this._sw1, this._sw2, this._sw3];
    }
}